// FrequentationChart component
// CREATED: Analytics v3 - single area/line chart for frequentation evolution;
//   replaces the list of MissionFrequentationCard bar charts
// UPDATED: Fixed Y-axis label truncation by setting left margin to 0 instead of -16

import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { MissionDayPoint } from '../../lib/analytics';

interface FrequentationChartProps {
  dailySeries: MissionDayPoint[];
  missionName: string;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

interface TooltipPayloadEntry {
  payload: MissionDayPoint;
  value: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md px-3 py-2.5 text-xs">
      <p className="font-semibold text-gray-600 mb-1">{formatDate(d.date)}</p>
      <p className="text-gray-900">
        <span className="font-bold text-base">{d.total}</span>
        <span className="text-gray-500 ml-1">participants</span>
      </p>
      {d.sessionCount > 1 && (
        <p className="text-gray-400 mt-0.5">{d.sessionCount} séances ce jour</p>
      )}
    </div>
  );
}

export function FrequentationChart({ dailySeries, missionName }: FrequentationChartProps) {
  const hasMultiSession = dailySeries.some(p => p.sessionCount > 1);

  const chartData = dailySeries.map(p => ({
    ...p,
    label: formatDate(p.date)
  }));

  if (chartData.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
        <p className="text-sm text-gray-400">Aucune donnée sur cette période</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 pt-4 pb-3">
      <p className="text-xs font-medium text-gray-500 mb-4 truncate">{missionName}</p>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="freqGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3E8B82" stopOpacity={0.18} />
                <stop offset="95%" stopColor="#3E8B82" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: '#9ca3af' }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#9ca3af' }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              width={32}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#3E8B82', strokeWidth: 1, strokeDasharray: '4 2' }} />
            <Area
              type="monotone"
              dataKey="total"
              stroke="#3E8B82"
              strokeWidth={2}
              fill="url(#freqGradient)"
              dot={chartData.length <= 14 ? { r: 3, fill: '#3E8B82', strokeWidth: 0 } : false}
              activeDot={{ r: 4, fill: '#3E8B82', strokeWidth: 2, stroke: '#fff' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {hasMultiSession && (
        <p className="text-xs text-gray-400 mt-2">
          Certains jours comptent plusieurs séances (total cumulé affiché)
        </p>
      )}
    </div>
  );
}
